/**
 * HighlightsPills - Feature Tags Row
 * 
 * Premium design:
 * - Horizontal scroll
 * - Lucide icons + text pills
 * - Soft backgrounds, no borders
 * - Clean, professional look
 */

import React from 'react';
import { Utensils, Clock, XCircle, Truck } from 'lucide-react';

interface HighlightsPillsProps {
  categories?: string[];
  isOpen: boolean;
  closingTime?: string;
  hasDelivery?: boolean;
}

interface PillData {
  icon: React.ReactNode;
  text: string;
  bgColor: string;
  iconColor: string;
}

export const HighlightsPills: React.FC<HighlightsPillsProps> = ({
  categories = [],
  isOpen,
  closingTime,
  hasDelivery,
}) => {
  // Build pills array with icons instead of emojis
  const pills: PillData[] = [];
  
  // Category pill
  if (categories.length > 0) {
    pills.push({
      icon: <Utensils className="h-3.5 w-3.5" strokeWidth={2} />,
      text: `Beliebt für ${categories.slice(0, 2).join(', ')}`,
      bgColor: 'rgba(14,127,79,0.08)',
      iconColor: 'text-emerald-600',
    });
  }
  
  // Open status pill
  if (isOpen && closingTime) {
    pills.push({
      icon: <Clock className="h-3.5 w-3.5" strokeWidth={2} />,
      text: `Geöffnet bis ${closingTime}`,
      bgColor: 'rgba(14,127,79,0.08)',
      iconColor: 'text-emerald-600',
    });
  } else if (!isOpen) {
    pills.push({
      icon: <XCircle className="h-3.5 w-3.5" strokeWidth={2} />,
      text: 'Aktuell geschlossen',
      bgColor: 'rgba(107,114,128,0.08)',
      iconColor: 'text-gray-500',
    });
  }
  
  // Delivery pill
  if (hasDelivery) {
    pills.push({
      icon: <Truck className="h-3.5 w-3.5" strokeWidth={2} />,
      text: 'Lieferung verfügbar',
      bgColor: 'rgba(37,99,235,0.08)',
      iconColor: 'text-blue-600',
    });
  }
  
  if (pills.length === 0) return null;

  return (
    <section className="px-4 pt-5 pb-3">
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
        {pills.map((pill, index) => (
          <div
            key={index}
            className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full"
            style={{ 
              background: pill.bgColor,
              border: '1px solid rgba(0,0,0,0.04)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
            }}
          >
            <span className={pill.iconColor}>{pill.icon}</span>
            <span className="text-[13px] font-medium text-gray-800 whitespace-nowrap">
              {pill.text}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HighlightsPills;
